import {
  CustomPoiModel,
  CustomPoiCreatePayload,
  CustomPoiUpdatePayload,
  PresignedUploadRequest,
  PresignedUploadResponse,
  PoiSearchParams,
  PoiSearchResponse,
  CustomPoiCollectionModel,
  CustomPoiCollectionCreatePayload,
  CustomPoiCollectionUpdatePayload,
} from "../model/customPoi";
import { apiEndpoints } from "../constants";
import { apiDelete, apiPost, apiPut, useGet, apiGet } from "./method";
import { ApiResponseWithRefresh } from "./response";

// Get all Custom POIs (for current user/creator)
export const useApiCustomPois = () =>
  useGet<CustomPoiModel[]>(apiEndpoints.CUSTOM_POIS);

// Get single Custom POI by ID
export const useApiCustomPoi = (id: string): ApiResponseWithRefresh<CustomPoiModel> => {
  // Always call useGet (required by Rules of Hooks)
  const url = id && id !== "" ? apiEndpoints.CUSTOM_POIS + "/" + id : "";
  const result = useGet<CustomPoiModel>(url);

  // Return appropriate result based on ID
  return (!id || id === "")
    ? { data: null, loading: false, error: null, refresh: () => {} }
    : result;
};

// Create new Custom POI
export const apiCreateCustomPoi = async (data: CustomPoiCreatePayload) => {
  return await apiPost<CustomPoiModel>(apiEndpoints.CUSTOM_POIS, data);
};

// Update Custom POI
export const apiUpdateCustomPoi = async (id: number, data: CustomPoiUpdatePayload) => {
  return await apiPut<CustomPoiModel>(
    apiEndpoints.CUSTOM_POIS + "/" + id.toString(),
    data,
    null
  );
};

// Delete Custom POI
export const apiDeleteCustomPoi = async (id: number) => {
  return await apiDelete<any>(apiEndpoints.CUSTOM_POIS + "/" + id.toString());
};

// Publish Custom POI
export const apiPublishCustomPoi = async (id: number) => {
  return await apiPost<CustomPoiModel>(
    apiEndpoints.CUSTOM_POIS + "/" + id.toString() + "/publish",
    {}
  );
};

// Get presigned upload URL for audio or thumbnail
export const apiGetCustomPoiUploadUrl = async (request: PresignedUploadRequest) => {
  return await apiPost<PresignedUploadResponse>(
    apiEndpoints.CUSTOM_POIS + "/upload-url",
    request
  );
};

// Extract duration from uploaded audio file
export const apiExtractAudioDuration = async (s3Key: string, bucket: 'PUBLIC' | 'PODCAST' = 'PUBLIC') => {
  return await apiPost<{ duration: number; success: boolean }>(
    apiEndpoints.EXTRACT_AUDIO_DURATION,
    { s3Key, bucket }
  );
};

// Unified POI search (custom + google)
export const apiSearchPois = async (params: PoiSearchParams) => {
  const queryParams = new URLSearchParams();

  if (params.q) queryParams.append('q', params.q);
  if (params.latitude !== undefined) queryParams.append('latitude', params.latitude.toString());
  if (params.longitude !== undefined) queryParams.append('longitude', params.longitude.toString());
  if (params.radius !== undefined) queryParams.append('radius', params.radius.toString());
  if (params.type) queryParams.append('type', params.type);
  if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
  if (params.offset !== undefined) queryParams.append('offset', params.offset.toString());

  const url = apiEndpoints.POIS_SEARCH + (queryParams.toString() ? `?${queryParams.toString()}` : '');
  return await apiGet<PoiSearchResponse>(url);
};

// Get Custom POIs for a specific persona
export const apiGetCustomPoisByPersona = async (personaId: number) => {
  const queryParams = new URLSearchParams();
  queryParams.append('personaId', personaId.toString());

  const url = apiEndpoints.CUSTOM_POIS + `?${queryParams.toString()}`;
  return await apiGet<CustomPoiModel[]>(url);
};

// Get Custom POIs by status
export const apiGetCustomPoisByStatus = async (status: 'draft' | 'published' | 'archived') => {
  const queryParams = new URLSearchParams();
  queryParams.append('status', status);

  const url = apiEndpoints.CUSTOM_POIS + `?${queryParams.toString()}`;
  return await apiGet<CustomPoiModel[]>(url);
};

// Custom POI Collection API functions

// Get all Custom POI Collections
export const useApiCustomPoiCollections = () =>
  useGet<CustomPoiCollectionModel[]>(apiEndpoints.CUSTOM_POI_COLLECTIONS);

// Get single Custom POI Collection by ID
export const useApiCustomPoiCollection = (id: string) =>
  useGet<CustomPoiCollectionModel>(apiEndpoints.CUSTOM_POI_COLLECTIONS + "/" + id);

// Create new Custom POI Collection
export const apiCreateCustomPoiCollection = async (data: CustomPoiCollectionCreatePayload) => {
  return await apiPost<CustomPoiCollectionModel>(apiEndpoints.CUSTOM_POI_COLLECTIONS, data);
};

// Update Custom POI Collection
export const apiUpdateCustomPoiCollection = async (id: number, data: CustomPoiCollectionUpdatePayload) => {
  return await apiPut<CustomPoiCollectionModel>(
    apiEndpoints.CUSTOM_POI_COLLECTIONS + "/" + id.toString(),
    data,
    null
  );
};

// Delete Custom POI Collection
export const apiDeleteCustomPoiCollection = async (id: number) => {
  return await apiDelete<any>(apiEndpoints.CUSTOM_POI_COLLECTIONS + "/" + id.toString());
};

// Publish Custom POI Collection
export const apiPublishCustomPoiCollection = async (id: number) => {
  return await apiPost<CustomPoiCollectionModel>(
    apiEndpoints.CUSTOM_POI_COLLECTIONS + "/" + id.toString() + "/publish",
    {}
  );
};

// Get POIs in a Custom POI Collection
export const apiGetCustomPoiCollectionPois = async (id: number) => {
  const url = apiEndpoints.CUSTOM_POI_COLLECTIONS + "/" + id.toString() + "/pois";
  return await apiGet<CustomPoiModel[]>(url);
};

// Update POI order in a Custom POI Collection
export const apiUpdateCustomPoiCollectionOrder = async (id: number, poiIds: number[]) => {
  const url = apiEndpoints.CUSTOM_POI_COLLECTIONS + "/" + id.toString() + "/pois";
  return await apiPut<{ message: string; updated: number }>(url, { poiIds }, null);
};

// Upload file directly to backend
export const apiUploadCustomPoiFile = async (file: File, type: 'audio' | 'thumbnail', personaId: number, poiId?: number) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  formData.append('personaId', personaId.toString());
  if (poiId) {
    formData.append('poiId', poiId.toString());
  }

  return await apiPost<{ objectKey: string }>(
    apiEndpoints.CUSTOM_POIS + "/upload-file",
    formData,
    true // notJsonType - this is multipart/form-data
  );
};

// Upload file to presigned URL (fallback)
export const apiUploadFile = async (uploadUrl: string, file: File) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  }

  return response;
};
